import { FieldInstance, FieldInstanceForResolver, FieldTemplate, FieldTemplateForResolver, Image } from "db/definitions";
import { createFieldForBatchResolver } from "db/queries/fieldInstanceQueries";
import { createImageForBatchResolver } from "db/queries/imageQueries";
import { v4 as uuid } from "uuid";
import prepopulateCurrentDateAction from "./actions/prepopulateCurrentDateAction";
import { GeoLocationService } from "services/location/geoLocationService";
import { utmFromLatLon } from "./utmFromLatLon";

/**
 * Build out the objects for the table row's field instances.
 * @param parentFieldInstance the row of the table to use as a template create the new row.
 * @param headerMap containing all templates in row
 * @param online Whether or not we're online
 * @param projectId The id of the project that houses all of these documents. We need this for the created images to have their index
 * @returns object with the fields to be created (fieldArray) and the
 * full denormalized fields (fullFieldArray).
 */
async function buildTableFieldsObjects(
	parentFieldInstance: FieldInstance,
	headerMap: Record<string, FieldTemplate>,
	online: boolean,
	projectId: string,
	linkedAltIds?: Record<string, string>,
	linkedData?: Record<string, FieldInstance>,
): Promise<{ fieldArray: FieldInstanceForResolver[]; fullFieldArray: FieldInstance[]; images: Image[]; rowString: string }> {
	const fieldArray = [];
	const fullFieldArray = [];

	const rowIdArray = [];
	const imageArray = [];
	if (parentFieldInstance.fieldTemplate?.rowOrder) {
		const rowOrder = parentFieldInstance.fieldTemplate.rowOrder;
		for (let i = 0; i < rowOrder.length; i++) {
			const rowFieldTemplateId = rowOrder[i];
			const rowFieldTemplate = headerMap[rowFieldTemplateId];
			let valueToUse = "";
			const childrenFields: FieldInstance[] = [];
			let newFieldsOrder: string[] = [];
			let cellLinkActionId: string | undefined;
			let cellLinkType: string | undefined;
			if (rowFieldTemplate.action && rowFieldTemplate.action.length > 0) {
				// if the row field template has an action then we may need to do something.
				// Right now its only the prepopulate action here.
				for (let ii = 0; ii < rowFieldTemplate.action.length; ii++) {
					const actionStatement = rowFieldTemplate.action[ii];
					if (actionStatement === "PREPOPULATE" || actionStatement === "PREPOPULATE_PICKLIST") {
						// If the action is prepopulate, then we need to use the value from the action data at the same index.
						valueToUse = rowFieldTemplate.actionData[ii] || "";
					}

					if (actionStatement === "PREPOPULATE_CURRENT_DATE") {
						valueToUse = prepopulateCurrentDateAction();
					}

					if (
						actionStatement === "PREPOPULATE_LOCATION" &&
						(rowFieldTemplate.inputType === "geospatial" || rowFieldTemplate.inputType === "geospatialutm")
					) {
						// ITS A GEO FIELD!!
						// THIS COULD GET WEIRD BECAUSE THIS IS THE FIRST TABLE FIELD THAT WILL HAVE CHILDREN FIELDS....
						// I think the children fields need to be put into the fieldArray still as does the parent.
						// but then the children fields need to be just included on the final parent field as children maybe..
						// PARENT TABLE WILL NEED FIELDS ORDER after the children are created.

						// if its geospatial lets make sure there is a 1st row with fields order setup

						// Also need to check the action on the geospatial field template to see if
						// PREPOPULATE_LOCATION action is set, then we need to grab the location data and set to its children
						// grab the location data from wherever it is stored (likely from the form group or some geo service)
						// and set it to the children fields of this geospatial field instance

						// Commenting this out for development purposes.
						const locationData = await GeoLocationService.getCurrentPosition();

						// For desktop basic functionality testing without having access to locations api on device -
						// const locationData = {
						// 	status: "success",
						// 	latitude: 37.77,
						// 	longitude: -122.4197,
						// 	accuracy: 5,
						// }

						// NOW NEED TO SETUP EACH CHIL FIELD INSTANCE AND SET VALUES TO CORRECT SPOT.
						// let newValue = "";
						if (locationData.status === "success") {
							// show confirmation UI
							const { latitude, longitude, accuracy } = locationData;
							if (rowFieldTemplate.inputType === "geospatial") {
								valueToUse = JSON.stringify({ lat: latitude, lon: longitude });
							} else if (rowFieldTemplate.inputType === "geospatialutm") {
								const { zoneNumber, hemisphere, easting, northing } = utmFromLatLon(latitude, longitude);

								valueToUse = JSON.stringify({
									easting,
									northing,
									zone: zoneNumber,
									hemisphere,
									datum: "WGS84",
									source: "device",
									wgs84: { lat: latitude, lon: longitude, accuracy },
								});
							}
						}
					}
					if (actionStatement === "LINK") {
						cellLinkActionId = rowFieldTemplate.actionData[ii];
						cellLinkType = "LINK";
					}
					if (actionStatement === "LINK_TO") {
						cellLinkActionId = `${parentFieldInstance.formInstance}_${rowFieldTemplate.actionData[ii]}`;
						cellLinkType = "LINK_TO";
					}
				}
			}

			const id = uuid();
			rowIdArray.push(id);
			const date = new Date().toISOString();
			const fieldObj: FieldInstanceForResolver = {
				id,
				fieldTemplateId: rowFieldTemplateId,
				formGroup: parentFieldInstance.formGroup,
				tableField: parentFieldInstance.id,
				group: parentFieldInstance.group,
				value: valueToUse,
				fieldsOrder: [...newFieldsOrder],
				notes: "",
				authors: [],
				lastAuthor: "",
				editing: "",
				createdAt: date,
				updatedAt: date,
			};
			let cellAltInstance: FieldInstanceForResolver | undefined;
			if (cellLinkType && cellLinkActionId) {
				const existingAltId = linkedAltIds?.[cellLinkActionId];
				if (existingAltId) {
					fieldObj.altInstanceId = existingAltId;
					cellAltInstance = linkedData?.[existingAltId] as FieldInstanceForResolver | undefined;
				} else {
					const altId = uuid();
					const newAltInstance: FieldInstanceForResolver = {
						id: altId,
						formGroup: fieldObj.formGroup,
						value: "",
						authors: [],
						group: fieldObj.group,
						fieldTemplateId: null,
						fieldsOrder: null,
						notes: "",
						editing: "",
						createdAt: date,
						updatedAt: date,
					};
					fieldObj.altInstanceId = altId;
					fieldArray.push(newAltInstance);
					cellAltInstance = newAltInstance;
					if (linkedAltIds) linkedAltIds[cellLinkActionId] = altId;
				}
			}
			const fieldForResolver = createFieldForBatchResolver(fieldObj) as FieldInstance;

			if (rowFieldTemplate.inputType === "image") {
				const imageDocId = uuid();
				const badImage: Image = {
					id: imageDocId,
					dpi: "",
					aspectX: "",
					aspectY: "",
					rotation: "",
					key: "",
					formGroup: parentFieldInstance.formGroup,
					project: projectId,
					group: parentFieldInstance.group,
					createdAt: date,
					updatedAt: date,
				};
				const imageDoc = createImageForBatchResolver(badImage);

				imageArray.push(imageDoc);
				fieldForResolver.imageId = imageDocId;
				fieldForResolver.image = imageDoc;
			}
			// Since the image is removed by the createFieldForBatchResolver function, I make a copy of it here to put back on the fieldAfter for the fullFieldObj
			const image = fieldForResolver.image ? { ...fieldForResolver.image } : undefined;
			const tableField = createFieldForBatchResolver(fieldForResolver);
			if (!online) tableField.touched = 1;
			fieldArray.push(tableField);
			const fullFieldObj: FieldInstance = {
				...tableField,
				fieldTemplate: rowFieldTemplate,
				tableFields: {
					items: childrenFields,
				},
			};
			if (image) fullFieldObj.image = image;
			if (cellAltInstance) {
				fullFieldObj.altInstance = cellAltInstance;
				fullFieldObj.altInstanceId = cellAltInstance.id;
			} else if (fieldObj.altInstanceId) {
				fullFieldObj.altInstanceId = fieldObj.altInstanceId;
				if (linkedData?.[fieldObj.altInstanceId]) {
					fullFieldObj.altInstance = linkedData[fieldObj.altInstanceId];
				}
			}

			fullFieldArray.push(fullFieldObj);
		}
	}
	const rowString = rowIdArray.join(",");

	return {
		fieldArray,
		fullFieldArray,
		images: imageArray,
		rowString,
	};
}

export default buildTableFieldsObjects;
